import { Add, Check, Delete } from '@mui/icons-material'
import { Button, FormControl, FormHelperText, TextField, Typography } from '@mui/material'
import { grey, red } from '@mui/material/colors'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { selectAuthUser } from '../auth/AuthSlice'
import { pickAdminOrg, resetAdminOrg, saveAdminOrgAsync, selectAdminOrg, selectAllAdminOrgs } from './AdminOrgSlice'

const AdminOrgEditor = () => {
  const { oid, operation } = useParams()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const adminOrgs = useAppSelector(selectAllAdminOrgs)
  const selectedAdminOrg = useAppSelector(selectAdminOrg)
  const user = useAppSelector(selectAuthUser)

  const [paidTier, setPaidTier] = useState<any>('')
  const [paidTierCount, setPaidTierCount] = useState<any>('')
  const [refillCount, setRefillCount] = useState<any>('')
  const [autoAddEmailPattern, setAutoAddEmailPattern] = useState<any>('')
  const [paidTierExpiry, setPaidTierExpiry] = useState<any>('')
  const [ownerId, setOwnerId] = useState<any>('')
  const [ownerEmail, setOwnerEmail] = useState<any>('')
  const [orgName, setOrgName] = useState<any>('')
  const [sharedOrgs, setSharedOrgs] = useState<any>('')

  const paidTierIp = useRef<any>(null)
  const paidTierCountIp = useRef<any>(null)
  const refillCountIp = useRef<any>(null)
  const autoAddEmailPatternIp = useRef<any>(null)
  const paidTierExpiryIp = useRef<any>(null)
  const ownerIdIp = useRef<any>(null)
  const ownerEmailIp = useRef<any>(null)
  const orgNameIp = useRef<any>(null)
  const sharedOrgsIp = useRef<any>(null)

  useEffect(() => {
    const selectedAdminOrg = oid && adminOrgs.content.find((u) => u.id === parseInt(oid))
    if (selectedAdminOrg) {
      dispatch(pickAdminOrg(selectedAdminOrg))
      setOrgName(selectedAdminOrg.name)
      setPaidTier(selectedAdminOrg.paidTier)
      setPaidTierCount(selectedAdminOrg.paidTierCount)
      setRefillCount(selectedAdminOrg.refillCount)
      setPaidTierExpiry(selectedAdminOrg.paidTierExpiry * 1000)
      setAutoAddEmailPattern(selectedAdminOrg.autoAddEmailPattern)
      setSharedOrgs(selectedAdminOrg.sharedOrgs)
    }
  }, [oid, adminOrgs])

  return (<div>
    {selectedAdminOrg && <>
      {oid === "0" ? <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
        Add new Org
      </Typography> : <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
        {operation === 'delete' ? 'Deleting' : 'Editing'} <i>{selectedAdminOrg.name}</i> (id: {selectedAdminOrg.id})
      </Typography>}
      {oid === "0" && operation !== 'mynew' && <>
        <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
          <TextField id="ip-text-owner-id" label="Owner Id" variant="outlined"
            name="owner-id" type="text" ref={ownerIdIp} value={ownerId ?? ''}
            onChange={(e: any) => setOwnerId(e.target.value)} />
          <FormHelperText id="helper-text-owner-id">Firebase id of the owner</FormHelperText>
        </FormControl>
        <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
          <TextField id="ip-text-owner-email" label="Owner Email" variant="outlined"
            name="owner-email" type="text" ref={ownerEmailIp} value={ownerEmail ?? ''}
            onChange={(e: any) => setOwnerEmail(e.target.value)} />
          <FormHelperText id="helper-text-owner-email">Email address of the owner</FormHelperText>
        </FormControl>
      </>}
      <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
        <TextField id="ip-text-owner-id" label="Org Name" variant="outlined" disabled={operation === 'delete'}
          name="org-name" type="text" ref={orgNameIp} value={orgName ?? ''}
          onChange={(e: any) => setOrgName(e.target.value)} />
        <FormHelperText id="helper-text-owner-id">Name of the Org</FormHelperText>
      </FormControl>
      {operation !== 'mynew' && <>
        <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
          <TextField id="ip-text-paid-tier-count" label="Number of properties remaining" variant="outlined" disabled={operation === 'delete'}
            name="paid-tier-count" onChange={(e: any) => setPaidTierCount(e.target.value)} type="number"
            ref={paidTierCountIp} value={paidTierCount ?? ''} />
          <FormHelperText id="helper-text-paid-tier-count">Number of reports allowed</FormHelperText>
        </FormControl>

        <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker label="Expires on" disabled={operation === 'delete'}
              onChange={(newValue: any) => setPaidTierExpiry(newValue)}
              ref={paidTierExpiryIp} value={paidTierExpiry ?? 0} />
          </LocalizationProvider>
          <FormHelperText id="helper-text-paid-tier-expiry">Paid Tier expires on</FormHelperText>
        </FormControl>

        <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
          <TextField id="ip-text-refill-count" label="Number of properties to refill automativally every month, set as 0 if not required" variant="outlined" disabled={operation === 'delete'}
            name="refill-count" onChange={(e: any) => setRefillCount(e.target.value)} type="number"
            ref={refillCountIp} value={refillCount ?? '0'} />
          <FormHelperText id="helper-text-refill-count">Number of properties to refill automativally every month, set as 0 if not required</FormHelperText>
        </FormControl>
      </>}

      <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
        <TextField id="ip-shared-orgs-id" label="Share credits with these email domains (eg: sfdc.com, rfa.com)" variant="outlined" disabled={operation === 'delete'}
          name="shared-orgs" type="text" ref={sharedOrgsIp} value={sharedOrgs ?? ''}
          onChange={(e: any) => setSharedOrgs(e.target.value)} />
        <FormHelperText id="helper-shared-orgs-id">You can share the org's credits with these email domains, comma separated. Owners email domain is implicit.</FormHelperText>
      </FormControl>

      {user && user.authorities?.some(e => e.authority === 'ROLE_ADMIN') ?
        <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
          <TextField id="ip-text-auto-add-email-pattern" label="Email domain to auto add users from, into this Org" variant="outlined" disabled={operation === 'delete'}
            name="auto-add-email-pattern" onChange={(e: any) => setAutoAddEmailPattern(e.target.value)} type="text"
            ref={autoAddEmailPatternIp} value={autoAddEmailPattern ?? ''} />
          <FormHelperText id="helper-text-auto-add-email-pattern">Email domain to auto add users from, into this Org, example: <i>{user?.email?.substring(user?.email.indexOf('@') + 1)}</i> </FormHelperText>
        </FormControl> : <>
          <Typography variant="caption" component="div" sx={{ flexGrow: 1, color: grey[500], mb: 4 }}>{autoAddEmailPattern}</Typography>
        </>
      }

      {operation === 'delete' && <>
        <Typography variant='h4' sx={{ mb: 3, color: red[800] }}>This is <strong>DANGEROUS</strong> and has very <strong>BAD</strong> consequences.</Typography>
        <Typography variant='body1' sx={{ mb: 3, color: red[800] }}>Are you <strong>REALLY</strong> sure?</Typography>
      </>
      }

      <Button color={operation === 'delete' ? 'error' : 'primary'}
        variant={operation === 'delete' ? 'contained' : 'outlined'}
        endIcon={operation === 'delete' ? <Delete /> : oid === '0' ? <Add /> : <Check />} sx={{ marginRight: 2 }} onClick={() => {
          dispatch(saveAdminOrgAsync({
            adminOrg: {
              ...selectedAdminOrg,
              name: orgName,
              ownerId: oid === "0" ? ownerId : selectedAdminOrg.ownerId,
              ownerEmail: oid === "0" ? ownerEmail : selectedAdminOrg.ownerEmail,
              paidTier: oid === "0" ? 0 : paidTier,
              paidTierCount,
              refillCount,
              ...(paidTierExpiry && { paidTierExpiry: typeof paidTierExpiry === 'number' ? paidTierExpiry / 1000 : paidTierExpiry.getTime() / 1000 }),
              autoAddEmailPattern,
              sharedOrgs
            },
            mode: oid === '0' && operation !== 'mynew' ? 'new' : operation ? operation : 'edit'
          }))
          if (operation === 'delete') {
            navigate({ 'pathname': `/home` })
          } else {
            navigate({ 'pathname': `/admin/orgs` })
          }
        }}>{operation === 'delete' ? 'Delete' : 'Save'}</Button>
      <Button variant="outlined" sx={{ marginRight: 2 }} onClick={() => {
        dispatch(resetAdminOrg())
        navigate({ 'pathname': `/admin/orgs` })
      }}>Cancel</Button>
    </>}
  </div>)
}

export default AdminOrgEditor